// Ranking configuration (PRODUCT.md "Ranking"). Stored in `ranking_config`
// (key = 'weights'), editable by curators; these are the defaults seeded on
// first run. Weights are points out of 100; bonuses stack on top (capped).
import { z } from 'zod';

import type { Seniority } from './enums.ts';

export const rankingWeightsSchema = z.object({
  roleType: z.number().int().min(0).max(100),
  requiredSkills: z.number().int().min(0).max(100),
  niceToHaveSkills: z.number().int().min(0).max(100),
  seniority: z.number().int().min(0).max(100),
  timezoneOverlap: z.number().int().min(0).max(100),
  salary: z.number().int().min(0).max(100),
  industryWorkedIn: z.number().int().min(0).max(100),
  industryInterestedIn: z.number().int().min(0).max(100),
  /** Bonus for availability immediately / within 1 month. */
  availabilityBonus: z.number().int().min(0).max(20),
  /** Matches below this are not written. */
  minScore: z.number().int().min(0).max(100),
  /** Working day length used for the timezone overlap rule. */
  workdayHours: z.number().int().min(4).max(12),
});
export type RankingWeights = z.infer<typeof rankingWeightsSchema>;

export const DEFAULT_RANKING_WEIGHTS: RankingWeights = {
  roleType: 25,
  requiredSkills: 25,
  niceToHaveSkills: 5,
  seniority: 15,
  timezoneOverlap: 10,
  salary: 10,
  industryWorkedIn: 5,
  industryInterestedIn: 5,
  availabilityBonus: 3,
  minScore: 40,
  workdayHours: 8,
};

/** Keys of `score_breakdown`, in display order. */
export const RANKING_RULES = [
  'roleType',
  'requiredSkills',
  'niceToHaveSkills',
  'seniority',
  'timezoneOverlap',
  'salary',
  'industryWorkedIn',
  'industryInterestedIn',
  'availabilityBonus',
] as const;
type RankingRule = (typeof RANKING_RULES)[number];

interface ScoreBreakdownEntry {
  /** Max points this rule can give. */
  weight: number;
  /** Points awarded (0..weight). */
  score: number;
  /** Human-readable reason. */
  detail: string;
}
export type ScoreBreakdown = Record<RankingRule, ScoreBreakdownEntry>;

/**
 * Structured facts behind one match, written with `score_breakdown` at rank
 * time (`match.facts`) so curator views don't parse the breakdown's text.
 * A snapshot: it changes only when the role is ranked again.
 */
export interface MatchFacts {
  requiredHave: number;
  requiredTotal: number;
  niceHave: number;
  niceTotal: number;
  /** Names of the role's required/nice-to-have skills the candidate has, A–Z. */
  requiredMatched: string[];
  niceMatched: string[];
  /** Seniority the candidate targets for this role's type (null if none). */
  candidateSeniority: Seniority | null;
  /** Shared working hours with the team (null when the role sets no overlap). */
  overlapHours: number | null;
}
